import { useState } from 'react'
import { cn, getCustomInputClass, updateFontSizes } from '@utils'
import { InputType } from '@/constants'
import { useEnhancedPathInputs } from '@hooks'
import ChoiceFields from './ChoiceFields'
import InputButton from './InputButton'
import StepTitle from '@/components/StepTitle'
import StepMedia from './StepMedia'

// Generic funnel-step renderer: turns a session's valid_inputs/additional_inputs
// into buttons, or a ChoiceFields list for Radio/Select steps. Shared by the
// funnel's popup flow and the OMP action drawer so both render backend steps
// identically.
const StepInputArea = ({
  session,
  onSelectInput,
  onBack,
}: {
  session: any
  onSelectInput: (input: any, inputData?: Record<string, any>) => void
  onBack?: () => void
}) => {
  // State, not a ref: the hook needs the node during effects, and a ref assigned
  // by the JSX below would still be null on the first run.
  const [root, setRoot] = useState<HTMLDivElement | null>(null)
  const inputs = (session?.valid_inputs as any[]) || []
  const additionalInputs = (session?.additional_inputs as any[]) || []
  const isEnhancedContent = !!session?.enhanced_mode
  const isEnhancedStep = isEnhancedContent && !!session?.content
  const showInlineInputs = (inputs.length > 0 || additionalInputs.length > 0) && !isEnhancedStep

  // An enhanced step's only controls are the path-inputs inside its own HTML.
  // Scoped to this subtree so a host nested in the funnel keeps its own callbacks.
  useEnhancedPathInputs({ session, onSelectInput, onBack, root })

  const isChoiceFields = [InputType.Radio, InputType.Select, InputType.RadioSelect].includes(Number(inputs[0]?.type))

  return (
    <div className="inputs" ref={setRoot}>
      <StepTitle
        session={session}
        subtitle={
          !isEnhancedContent && session?.subtitle ? (
            <div dangerouslySetInnerHTML={{ __html: updateFontSizes(session.subtitle) }} className="option rich-text" />
          ) : undefined
        }
        content={
          session?.content?.length ? (
            <div
              className={cn('w-full rich-text', isEnhancedContent && 'page-content')}
              dangerouslySetInnerHTML={{ __html: updateFontSizes(session.content as string) }}
            />
          ) : undefined
        }
        staticData={<StepMedia session={session} />}>
        {!isEnhancedContent && session?.label && (
          <div
            dangerouslySetInnerHTML={{ __html: updateFontSizes(session.label as string) }}
            className="how-can rich-text"
          />
        )}
      </StepTitle>

      {isEnhancedStep && onBack && session?.can_undo && (
        <div className="flex justify-center w-full py-4">
          <button type="button" className="btn-base back-btn" onClick={onBack}>
            Back
          </button>
        </div>
      )}

      {showInlineInputs &&
        (isChoiceFields ? (
          <ChoiceFields session={session} onSubmit={onSelectInput} onBack={onBack} />
        ) : (
          <div className="step-button-row flex flex-col gap-3 mb-3 mt-2">
            {inputs.map((input: any) => (
              <InputButton
                key={`step-input-${input.id}`}
                input={input}
                className={getCustomInputClass(session, input, false)}
                onClick={() => onSelectInput(input)}
              />
            ))}
            {additionalInputs.map((input: any) => (
              <InputButton
                key={`step-additional-${input.id}`}
                input={input}
                className={cn('btn-outline-success', getCustomInputClass(session, input, false))}
                onClick={() => onSelectInput(input)}
              />
            ))}
          </div>
        ))}
    </div>
  )
}

export default StepInputArea
